import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDate, calculateAge, debounce } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Search,
  Plus,
  Filter,
  Download,
  Users,
  ChevronRight,
  Phone,
  Mail,
  X,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function PatientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    gender: searchParams.get('gender') || '',
    blood_type: searchParams.get('blood_type') || '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchPatients = async (search = '', filterParams = {}) => {
    setLoading(true);
    try {
      const params = { search, ...filterParams };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      
      const response = await patientsAPI.getAll(params);
      setPatients(response.data);
    } catch (err) {
      toast.error('Failed to fetch patients');
    }
    setLoading(false);
  };

  const debouncedSearch = debounce((query) => {
    setSearchParams(prev => {
      if (query) prev.set('search', query);
      else prev.delete('search');
      return prev;
    });
    fetchPatients(query, filters);
  }, 300);

  useEffect(() => {
    fetchPatients(searchQuery, filters);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setSearchParams(prev => {
      if (value) prev.set(key, value);
      else prev.delete(key);
      return prev;
    });
    fetchPatients(searchQuery, newFilters);
  };

  const clearFilters = () => {
    setFilters({ gender: '', blood_type: '' });
    setSearchParams(prev => {
      prev.delete('gender');
      prev.delete('blood_type');
      return prev;
    });
    fetchPatients(searchQuery, {});
  };

  const hasActiveFilters = filters.gender || filters.blood_type;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="patients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Patients</h1>
          <p className="text-muted-foreground mt-1">
            Manage and view patient records
          </p>
        </div>
        {hasPermission('patients:create') && (
          <Button onClick={() => navigate('/patients/new')} className="gap-2" data-testid="add-patient-btn">
            <Plus className="h-4 w-4" />
            Add Patient
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, ID, email, or phone..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-9"
                data-testid="patient-search"
              />
            </div>

            {/* Filter Toggle */}
            <div className="flex gap-2">
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
                className="gap-2"
                data-testid="filter-toggle"
              >
                <Filter className="h-4 w-4" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center">
                    {Object.values(filters).filter(Boolean).length}
                  </Badge>
                )}
              </Button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 items-end animate-fadeIn">
              <div className="space-y-2">
                <label className="text-sm font-medium">Gender</label>
                <Select 
                  value={filters.gender || "all"} 
                  onValueChange={(v) => handleFilterChange('gender', v === "all" ? "" : v)}
                >
                  <SelectTrigger className="w-[140px]" data-testid="gender-filter">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Blood Type</label>
                <Select 
                  value={filters.blood_type || "all"} 
                  onValueChange={(v) => handleFilterChange('blood_type', v === "all" ? "" : v)}
                >
                  <SelectTrigger className="w-[140px]" data-testid="blood-type-filter">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="A+">A+</SelectItem>
                    <SelectItem value="A-">A-</SelectItem>
                    <SelectItem value="B+">B+</SelectItem>
                    <SelectItem value="B-">B-</SelectItem>
                    <SelectItem value="AB+">AB+</SelectItem>
                    <SelectItem value="AB-">AB-</SelectItem>
                    <SelectItem value="O+">O+</SelectItem>
                    <SelectItem value="O-">O-</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {loading ? 'Loading...' : `${patients.length} Patient${patients.length !== 1 ? 's' : ''}`}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : patients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
              <h3 className="text-lg font-medium mb-1">No patients found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'Get started by adding your first patient'}
              </p>
              {hasPermission('patients:create') && !searchQuery && !hasActiveFilters && (
                <Button onClick={() => navigate('/patients/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Patient
                </Button>
              )}
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead className="hidden md:table-cell">ID</TableHead>
                    <TableHead className="hidden sm:table-cell">Age / DOB</TableHead>
                    <TableHead className="hidden lg:table-cell">Contact</TableHead>
                    <TableHead className="hidden md:table-cell">Blood Type</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patients.map((patient) => (
                    <TableRow
                      key={patient.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/patients/${patient.id}`)}
                      data-testid={`patient-row-${patient.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="font-semibold text-sm text-primary">
                              {patient.first_name[0]}{patient.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">
                              {patient.first_name} {patient.last_name}
                            </p>
                            <p className="text-sm text-muted-foreground capitalize">
                              {patient.gender}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {patient.patient_id}
                        </code>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div>
                          <p className="font-medium">{calculateAge(patient.date_of_birth)} yrs</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(patient.date_of_birth)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="space-y-1">
                          {patient.phone && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              {patient.phone}
                            </div>
                          )}
                          {patient.email && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              <span className="truncate max-w-[150px]">{patient.email}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {patient.blood_type ? (
                          <Badge variant="outline">{patient.blood_type}</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
